import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { teamApi } from "@/lib/admin-api/team";
import type { InviteAdminInput } from "@/lib/admin-api/team";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  team: (params: { q?: string; page?: number } = {}) =>
    ["admin-team", params] as const,
} as const;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useTeam(params: { q?: string; page?: number } = {}) {
  return useQuery({
    queryKey: KEYS.team(params),
    queryFn: () => teamApi.list(params),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useInviteAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InviteAdminInput) => teamApi.invite(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
      toast.success("Administrateur créé", {
        description: result.note,
      });
    },
    onError: (err: Error) => {
      toast.error("Erreur lors de l'invitation", {
        description: err.message ?? "Une erreur est survenue.",
      });
    },
  });
}

export function useUpdateAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{ role: string; department_id: string; is_active: boolean; full_name: string }>;
    }) => teamApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
      toast.success("Administrateur mis à jour");
    },
    onError: (err: Error) => {
      toast.error("Erreur lors de la mise à jour", {
        description: err.message ?? "Une erreur est survenue.",
      });
    },
  });
}

export function useDeactivateAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teamApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
      toast.success("Administrateur désactivé");
    },
    onError: (err: Error) => {
      toast.error("Erreur lors de la désactivation", {
        description: err.message ?? "Une erreur est survenue.",
      });
    },
  });
}
